import { Component, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map, takeUntil, tap } from 'rxjs/operators';

import { PageNames } from 'src/app/configs/page-names.enum';
import { QueryStringTypesEnum } from 'src/app/configs/query-string-types';
import { CustomResultOfPaginatedListOfGetGroupPriceCategoriesWithPaginationDto } from 'src/app/core/api/models';
import { OrganizationsService } from 'src/app/core/api/services';
import { GetGroupPriceCategoriesWithPaginationDto } from '../../../core/api/models/get-group-price-categories-with-pagination-dto';
import { PaginatedListOfGetGroupPriceCategoriesWithPaginationDto } from '../../../core/api/models/paginated-list-of-get-group-price-categories-with-pagination-dto';
import { GroupPriceCategoriesService } from '../../../core/api/services/group-price-categories.service';
import { GroupPriceDetailsService } from '../../../core/api/services/group-price-details.service';
import { GroupPricesService } from '../../../core/api/services/group-prices.service';
import { AppDomService } from '../../../core/services/dom.service';
import { BaseIndexPageComponent } from '../../shared/base/components/base-index-page.component';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { GroupPriceCategoriesFormComponent } from './group-price-categories-form/group-price-categories-form.component';
import { GroupPriceComponent } from './group-price-list/group-price.component';
import { GroupPriceOrganizationsComponent } from './group-price-organizations/group-price-organizations.component';

@Component({
  selector: 'app-group-price-categories',
  imports: [
    BaseIndexModule,
    GroupPriceOrganizationsComponent,
    GroupPriceComponent,
  ],
  templateUrl: './group-price-categories.component.html',
})
export class GroupPriceCategoriesComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties

  pageName = PageNames.groupPrice;

  // binding component CRUD endpoints
  protected currentService = inject(GroupPriceCategoriesService);
  createGetApiMethodName: string =
    this.currentService.groupPriceCategoriesCreateGet.name;
  auditApiMethodName: string =
    this.currentService
      .groupPriceCategoriesGetGroupPriceCategoriesAuditWithPagination.name;
  deleteApiMethodName: string =
    this.currentService.groupPriceCategoriesDelete.name;
  // binding component CRUD endpoints

  modelWithPagination$: Observable<PaginatedListOfGetGroupPriceCategoriesWithPaginationDto>;
  items$: Observable<Array<GetGroupPriceCategoriesWithPaginationDto>>;

  isShowGroupPrices: boolean;
  isShowOrganizations: boolean;
  isShowGroupPriceDetailAuditPage: boolean;
  isShowGroupPriceAuditPage: boolean;

  selectedCategoryId: number;
  selectedCategoryName: string;
  groupPriceOrganizations: number[] = [];

  //#endregion

  constructor(
    public groupPricesService: GroupPricesService,
    private organizationsService: OrganizationsService,
    public groupPriceDetailService: GroupPriceDetailsService
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.pageConfig.showAuditBtn = false;
    this.getUserState();
    this.pageConfig.title = this.pageVocabs + 'groupPriceCategories.title';
  }

  configTableColumns() {
    this.tableColumns = [
      { title: this.localizer.title, name: 'categoryName', hasSearch: true },
      {
        title: `${this.pageVocabs}groupPriceCategories.groupPriceCount`,
        name: 'groupPriceCount',
        hasSearch: true,
        type: 'number',
        noSorting: true,
      },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
      },
    ];
  }

  // وارد میشود چک میکنیم که کدام جدول باید نمایش داده شود url زمانی که کاربر با
  setPageAfterEnterUrl() {
    super.setPageAfterEnterUrl();
    let params = this.routingState.parseQueryString();
    for (const [key, value] of Object.entries(params)) {
      if (key == QueryStringTypesEnum.Clone_key)
        this.onShowGroupPrices(
          {
            id: +value.toString().split('_')[0],
            categoryName: value.toString().split('_')[1],
            groupPriceCount: +value.toString().split('_')[2],
            created: value.toString().split('_')[3],
          },
          false
        );
      if (key == QueryStringTypesEnum.Organization_key)
        this.onShowOrganizations(
          {
            id: +value.toString().split('_')[0],
            categoryName: value.toString().split('_')[1],
            groupPriceCount: +value.toString().split('_')[2],
            created: value.toString().split('_')[3],
          },
          false
        );
    }
  }
  //#region Api

  //Get All
  getPaginationApi(): Observable<CustomResultOfPaginatedListOfGetGroupPriceCategoriesWithPaginationDto> {
    if (window.location.search.includes('key-'))
      this.clearFiltersAndSetDefaultSort();

    return this.currentService.groupPriceCategoriesGetGroupPriceCategoriesWithPagination(
      { body: this.getBaseModelWithPaginationParams() }
    );
  }

  // getOrganizations
  getOrganizationsListByGroupPriceCategoryIdApi(categoryId: number) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.organizationsService
      .organizationsGetOrganizationsListByGroupPriceCategoryId({
        categoryId: categoryId,
      })
      .pipe(
        this.basePipeOperators(),
        map((items) => {
          return items.data.map((item) => item.id);
        })
      )
      .subscribe((res) => {
        this.groupPriceOrganizations = res;
        this.isShowOrganizations = true;
      });
  }

  //Get ById
  baseGetByIdApi(id: number): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = id;

    this.currentService
      .groupPriceCategoriesUpdateGet({ id: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseGetByIdObserverFormModal);
  }

  createGroupPriceCategoryByExistingCategoryGetApi(
    category: GetGroupPriceCategoriesWithPaginationDto
  ) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .groupPriceCategoriesCreateGroupPriceCategoryByExistingCategoryGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) this.openCopyGroupPriceCategoryModal(category);
      });
  }

  //#endregion

  openCreateFormModal() {
    let data = {
      isEdit: false,
    };

    const modalResult = this.dialogService.openModal({
      component: GroupPriceCategoriesFormComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.pageConfig.isVisibleForm = undefined)),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  openEditFormModal(category: GetGroupPriceCategoriesWithPaginationDto) {
    let data = {
      isEdit: true,
      categoryId: category.id,
      categoryName: category.categoryName,
    };

    const modalResult = this.dialogService.openModal({
      component: GroupPriceCategoriesFormComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  onDelete(category: GetGroupPriceCategoriesWithPaginationDto) {
    let dialogResult = this.dialogService.confirmDialog({
      content: category.categoryName,
      isConfirmDelete: true,
    });
    dialogResult.afterClosed().subscribe((result) => {
      if (result) {
        this.baseDeleteApi(category.id);
      }
    });
  }

  onCopy(category: GetGroupPriceCategoriesWithPaginationDto) {
    this.createGroupPriceCategoryByExistingCategoryGetApi(category);
  }

  openCopyGroupPriceCategoryModal(
    category: GetGroupPriceCategoriesWithPaginationDto
  ) {
    let data = {
      isEdit: false,
      categoryId: category.id,
      isUserForGroupPriceCopy: true,
    };

    const modalResult = this.dialogService.openModal({
      component: GroupPriceCategoriesFormComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  onShowGroupPrices(
    category: GetGroupPriceCategoriesWithPaginationDto,
    setUrl = true
  ) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Clone_key +
          '=' +
          category.id +
          '_' +
          category.categoryName +
          '_' +
          category.groupPriceCount +
          '_' +
          category.created
      );
    }
    this.selectedCategoryId = category.id;
    this.selectedCategoryName = category.categoryName;
    this.isShowGroupPrices = true;
  }

  onHideGroupPrices() {
    this.showParent = true;
    this.getParentQueryString();
    this.isShowGroupPrices = false;
  }

  onShowOrganizations(
    category: GetGroupPriceCategoriesWithPaginationDto,
    setUrl = true
  ) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Organization_key +
          '=' +
          category.id +
          '_' +
          category.categoryName +
          '_' +
          category.groupPriceCount +
          '_' +
          category.created
      );
    }
    this.selectedCategoryId = category.id;
    this.selectedCategoryName = category.categoryName;
    this.getOrganizationsListByGroupPriceCategoryIdApi(category.id);
  }

  //#region group price detail audit

  onShowGroupPriceDetailAuditPage() {
    this.isShowGroupPriceDetailAuditPage = true;
  }

  onShowGroupPriceAuditPage() {
    this.isShowGroupPriceAuditPage = true;
  }
  //#endregion
}
